import React from 'react'
import moment from 'moment'
import { authorization } from '../../../../../Module/Auth/Auth'
import { StatusLabel } from '../../../../../Components/StatusLabel'
import { LinkView } from '../../../../../Components/Link/View'
import { ButtonDeleteInline } from '../../../../../Components/Buttons/ButtonDeleteInline'
import {
  dropTimeZoneFromDate,
  getShortWeekDay,
  browserSafeNewDate,
} from '../../../../../shared/libraries/utils/date'
import { DATETIME_PICKER_FORMAT } from '../../../../../constants'
import { calculateTimeOfDay } from '../../../../../Utils/Mission'
import RosterMissionsTableView from '../component/RosterMissionsTableView'
import {
  dateSort,
  timeOfDaySort,
  dayOfWeekSort,
  statusSort,
} from '../../../../../Utils/Table'
import {
  STATUS_BOOKED,
  MISSION_STATUS_ENUM_MAP,
  MISSION_STATUS_ENUM,
} from '../../../../../shared/constants'
import {
  numberFormatter,
  numberFormatterWithOptional2Dp,
} from './../../../../../Utils/Formatters'
import { getLocalTimeSlotFromDateTime } from './../../../../../shared/mission'

const getDataForRosterMissionsTable = ({ missions, city }) => {
  const getMissionItemData = ({ mission, city }) => {
    const {
      id,
      location,
      cancelled,
      dateTime,
      flyerCode,
      totalFlyersDistributed,
      status = '',
      draftPayAdjustment = 0,
      automatic,
      user,
      amountScans,
    } = mission
    if (location) {
      var { name: locationName, detailedRank } = location
      var timeOfDay = getLocalTimeSlotFromDateTime(
        dropTimeZoneFromDate(dateTime),
      )
      try {
        detailedRank = JSON.parse(detailedRank)
      } catch (e) {
        detailedRank = {}
      }
      if (detailedRank == null) detailedRank = {}

      var rank =
        detailedRank[timeOfDay] != null ? detailedRank[timeOfDay] : null
      var typeAndRank = automatic ? 'A' : 'M'

      if (rank != null) {
        typeAndRank += ' ' + numberFormatterWithOptional2Dp(rank)
      }
    }
    const { firstName = '-', lastName = '' } = user || {}

    const flyerCodeValue = flyerCode || '-'
    const totalFlyersDistributedValue = totalFlyersDistributed || '-'
    const localDate = browserSafeNewDate(dropTimeZoneFromDate(dateTime))
    const draftPayAdjustmentValue =
      draftPayAdjustment === 0
        ? '-'
        : numberFormatterWithOptional2Dp(draftPayAdjustment / 100)

    return {
      id,
      'Name/Location': locationName || ' - ',
      'Day': getShortWeekDay(localDate.getDay()),
      'Date': moment(dropTimeZoneFromDate(dateTime)).format(
        DATETIME_PICKER_FORMAT,
      ),
      'Time of Day': calculateTimeOfDay(
        dateTime,
        city.lunchCheckInTime,
        city.afternoonCheckInTime,
      ),
      'Pay adjustment': draftPayAdjustmentValue,
      'Code': flyerCodeValue,
      'Flyers Distributed': totalFlyersDistributedValue,
      'Scans': String(amountScans),
      'Cancelled': cancelled,
      'Status': status,
      'BA': `${firstName} ${lastName}`,
      'Type & Rank': typeAndRank || ' - ',
      automatic,
      detailedRank: detailedRank ? detailedRank[timeOfDay] : null,
    }
  }

  return missions
    .slice(0)
    .sort(missionSort)
    .map(mission => getMissionItemData({ mission, city }))
}

const missionSort = function(a, b) {
  if (a.dateTime < b.dateTime) return -1
  if (a.dateTime > b.dateTime) return 1
  return 0
}

const getFooterData = missions => {
  const data = {
    'Name/Location': <strong>Total:</strong>,
    'Day': numberFormatter(missions.length),
    'Flyers Distributed': (
      <strong>
        {numberFormatter(
          missions.reduce((acc, mission) => {
            var distributed = Number(mission['Flyers Distributed'])
            return acc + (isNaN(distributed) ? 0 : distributed)
          }, 0),
        )}
      </strong>
    ),
  }
  const footerData = [data]
  return footerData
}

const RosterMissionsTable = ({
  missions,
  city,
  onDelete,
  history,
  ...props
}) => {
  const header = [
    'Name/Location',
    // 'Type & Rank', requested in https://www.pivotaltracker.com/story/show/173741791
    'Day',
    'Date',

    'Time of Day',
    !authorization.isClient() &&
      !authorization.isClientAdmin() &&
      'Pay adjustment',
    'Code',
    'Flyers Distributed',
    'Scans',
    'Status',
    'Type & Rank',
    'BA',
    'Actions',
  ]

  const sortOn = [
    'Name/Location',
    'BA',
    'Code',
    'Flyers Distributed',
    { label: 'Date', sort: dateSort },
    { label: 'Time of Day', sort: timeOfDaySort },
    { label: 'Day', sort: dayOfWeekSort },
    { label: 'Status', sort: statusSort },
  ]

  const filterOn = [
    'Name/Location',
    { label: 'Day', sort: dayOfWeekSort, sorted: false },
    { label: 'Time of Day', sort: timeOfDaySort, sorted: false },
    { label: 'Status', sort: statusSort },
  ]

  const data = getDataForRosterMissionsTable({ missions, city })

  const footerRender = getFooterData

  const searchOn = ['Name/Location', 'Code', 'BA']

  const postRowRender = mission => {
    var { detailedRank } = mission

    const statusNumber = MISSION_STATUS_ENUM_MAP[mission.Status]
    var automaticNote = mission.automatic
      ? `This mission is automatic the location and time of day may change.`
      : `This mission is manual, it won't change.`
    if (detailedRank != null)
      automaticNote += ` The location at this time of day is ranked ${numberFormatterWithOptional2Dp(
        detailedRank,
      )} out of 1.`
    else automaticNote += ` The location is unranked.`
    return {
      ...mission,
      'Status': (
        <StatusLabel
          status={
            mission.Cancelled && mission.Status != MISSION_STATUS_ENUM.CANCELLED
              ? `${MISSION_STATUS_ENUM.CANCELLED} / ${mission.Status}`
              : mission.Status
          }
        />
      ),
      'Actions': (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <LinkView />
          {(authorization.isAdmin() || authorization.isCityManager()) &&
            statusNumber < STATUS_BOOKED && (
              <ButtonDeleteInline
                onClickHandler={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  return onDelete(mission.id)
                }}
              />
            )}
        </div>
      ),
      'Type & Rank': (
        <span title={automaticNote} style={{ whiteSpace: 'nowrap' }}>
          {mission['Type & Rank']}
        </span>
      ),
    }
  }

  const action = mission =>
    window.open(`${props.match.url}/missions/${mission.id}`, '_blank').focus()

  return (
    <RosterMissionsTableView
      header={header}
      data={data}
      sortOn={sortOn}
      filterOn={filterOn}
      footerRender={footerRender}
      postRowRender={postRowRender}
      searchOn={searchOn}
      action={action}
    />
  )
}

export default RosterMissionsTable



// WEBPACK FOOTER //
// ./src/Containers/Campaigns/RosterView/RosterMissionsTable/container/RosterMissionsTable.js